import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { en } from '../../translations/en';
import { useAuth } from '../../hooks/useAuth';
import { useAliId } from '../../hooks/useAliId';
import { usePermissions } from '../../hooks/usePermissions';
import { useUserProfile } from '../../hooks/useUserProfile';

export const ProfileSection = () => {
  const { user } = useAuth();
  const { aliId } = useAliId();
  const { permissions } = usePermissions();
  const { profile, displayName, updateDisplayName, updateFullName, loading: profileLoading } =
    useUserProfile();

  const isViewer = permissions?.role === 'viewer';

  const getFullName = () => {
    if (profile?.firstName && profile?.lastName) {
      return `${profile.firstName} ${profile.lastName}`;
    }
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.username || 'Not available';
  };

  // Full name editing state
  const [editingFullName, setEditingFullName] = useState(false);
  const [firstNameInput, setFirstNameInput] = useState('');
  const [lastNameInput, setLastNameInput] = useState('');
  const [fullNameSaving, setFullNameSaving] = useState(false);
  const [fullNameError, setFullNameError] = useState<string | null>(null);
  const [fullNameSuccess, setFullNameSuccess] = useState(false);

  // Display name editing state
  const [editingDisplayName, setEditingDisplayName] = useState(false);
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [displayNameSaving, setDisplayNameSaving] = useState(false);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [displayNameSuccess, setDisplayNameSuccess] = useState(false);

  useEffect(() => {
    if (displayName !== null) {
      setDisplayNameInput(displayName);
    }
  }, [displayName]);

  // Full name handlers
  const handleFullNameEdit = () => {
    setEditingFullName(true);
    setFirstNameInput(profile?.firstName || user?.firstName || '');
    setLastNameInput(profile?.lastName || user?.lastName || '');
    setFullNameSuccess(false);
    setFullNameError(null);
  };

  const handleFullNameCancel = () => {
    setEditingFullName(false);
    setFirstNameInput('');
    setLastNameInput('');
    setFullNameError(null);
  };

  const handleFullNameSave = async () => {
    if (!firstNameInput.trim() || !lastNameInput.trim()) {
      setFullNameError('First name and last name are required');
      return;
    }
    try {
      setFullNameSaving(true);
      setFullNameError(null);
      await updateFullName(firstNameInput.trim(), lastNameInput.trim());
      setEditingFullName(false);
      setFullNameSuccess(true);
      setTimeout(() => setFullNameSuccess(false), 5000);
    } catch (err) {
      setFullNameError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setFullNameSaving(false);
    }
  };

  // Display name handlers
  const handleDisplayNameEdit = () => {
    setEditingDisplayName(true);
    setDisplayNameInput(displayName || '');
    setDisplayNameSuccess(false);
    setDisplayNameError(null);
  };

  const handleDisplayNameCancel = () => {
    setEditingDisplayName(false);
    setDisplayNameInput(displayName || '');
    setDisplayNameError(null);
  };

  const handleDisplayNameSave = async () => {
    if (!displayNameInput.trim()) {
      setDisplayNameError('Display name cannot be empty');
      return;
    }
    if (displayNameInput.length > 100) {
      setDisplayNameError('Display name must be 100 characters or less');
      return;
    }
    try {
      setDisplayNameSaving(true);
      setDisplayNameError(null);
      await updateDisplayName(displayNameInput.trim());
      setEditingDisplayName(false);
      setDisplayNameSuccess(true);
      setTimeout(() => setDisplayNameSuccess(false), 5000);
    } catch (err) {
      setDisplayNameError(err instanceof Error ? err.message : 'Failed to update display name');
    } finally {
      setDisplayNameSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 400 }}>
      {/* Full Name - Editable */}
      <Box>
        {fullNameSuccess && (
          <Typography color="success.main" sx={{ mb: 1, fontSize: '0.875rem' }}>
            Name updated successfully!
          </Typography>
        )}
        {fullNameError && (
          <Typography color="error.main" sx={{ mb: 1, fontSize: '0.875rem' }}>
            {fullNameError}
          </Typography>
        )}
        {editingFullName ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="First Name"
                value={firstNameInput}
                onChange={(e) => setFirstNameInput(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={fullNameSaving}
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                label="Last Name"
                value={lastNameInput}
                onChange={(e) => setLastNameInput(e.target.value)}
                fullWidth
                variant="outlined"
                disabled={fullNameSaving}
                inputProps={{ maxLength: 50 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleFullNameSave}
                disabled={fullNameSaving || !firstNameInput.trim() || !lastNameInput.trim()}
                startIcon={fullNameSaving ? <CircularProgress size={16} /> : undefined}
              >
                {fullNameSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleFullNameCancel}
                disabled={fullNameSaving}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                label={en.settings.account.profile.fullName}
                value={profileLoading ? 'Loading...' : getFullName()}
                fullWidth
                variant="outlined"
                disabled
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleFullNameEdit}
                disabled={profileLoading}
                sx={{ minWidth: 60, height: 56, mt: 0 }}
              >
                Edit
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* Display Name - Editable */}
      <Box>
        {displayNameSuccess && (
          <Typography color="success.main" sx={{ mb: 1, fontSize: '0.875rem' }}>
            Display name updated successfully!
          </Typography>
        )}
        {displayNameError && (
          <Typography color="error.main" sx={{ mb: 1, fontSize: '0.875rem' }}>
            {displayNameError}
          </Typography>
        )}
        {editingDisplayName ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              label="Display Name"
              value={displayNameInput}
              onChange={(e) => setDisplayNameInput(e.target.value)}
              fullWidth
              variant="outlined"
              disabled={displayNameSaving}
              helperText="This name will be shown as the creator on your objects"
              inputProps={{ maxLength: 100 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleDisplayNameSave}
                disabled={displayNameSaving || !displayNameInput.trim()}
                startIcon={displayNameSaving ? <CircularProgress size={16} /> : undefined}
              >
                {displayNameSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDisplayNameCancel}
                disabled={displayNameSaving}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                label="Display Name"
                value={profileLoading ? 'Loading...' : (displayName || '')}
                fullWidth
                variant="outlined"
                disabled
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleDisplayNameEdit}
                disabled={profileLoading}
                sx={{ minWidth: 60, height: 56, mt: 0 }}
              >
                Edit
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              This name will be shown as the creator on your objects
            </Typography>
          </Box>
        )}
      </Box>

      {/* ALI ID - Read Only */}
      {!isViewer && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Art Legacy Institute ID
          </Typography>
          <Typography variant="body1">
            {aliId || 'Loading...'}
          </Typography>
        </Box>
      )}

      {/* Email - Read Only */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          {en.settings.account.profile.email}
        </Typography>
        <Typography variant="body1">
          {user?.email || 'No email'}
        </Typography>
      </Box>
    </Box>
  );
};
